import { Avatar, Box } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Avatar que se agranda al pasarle el mouse, sin mover nada de alrededor.
 *
 * Crece con `transform: scale()` y no cambiando su tamaño: la transformación no
 * ocupa espacio en el layout, así que la fila no se reacomoda ni empuja al resto.
 * La imagen ampliada se **sobrepone** al contenido vecino, que es justamente la
 * gracia: en una tabla densa es la única forma de ver una cara sin abrir la ficha.
 *
 * Crece desde el borde izquierdo (`transform-origin`) y no desde el centro, porque
 * el avatar vive pegado al margen de la tabla: creciendo desde el centro, la mitad
 * se saldría por la izquierda.
 *
 * El aumento tiene techo, y por eso es 2x y no más: el contenedor de la tabla recorta
 * en vertical (`overflowY: hidden`, por el radio de las esquinas) y no se puede evitar
 * —con `overflowX: auto`, un `overflowY: visible` se convierte en `auto`—. A 2x la
 * foto queda dentro del alto de su fila; medido a 2.5x, la última fila se cortaba
 * 3 px contra el borde del contenedor.
 *
 * Lleva un anillo del color de la superficie para despegarla de lo que queda
 * debajo; sin él, la foto ampliada se lee encima del texto de la fila vecina.
 */
export default function AvatarAmpliable({
  src,
  nombre,
  tamano = 32,
  aumento = 2,
}: {
  src?: string | null;
  nombre: string;
  tamano?: number;
  aumento?: number;
}) {
  return (
    // La caja mantiene el tamaño original pase lo que pase: es la que ocupa el
    // espacio, y la que se transforma es la de adentro.
    <Box sx={{ width: tamano, height: tamano, flexShrink: 0, position: "relative" }}>
      <Avatar
        src={src ?? undefined}
        sx={{
          width: tamano,
          height: tamano,
          fontSize: tamano * 0.44,
          position: "absolute",
          inset: 0,
          transformOrigin: "left center",
          transition: "transform .18s ease",
          "&:hover": {
            transform: `scale(${aumento})`,
            zIndex: 3,
            boxShadow: `0 0 0 2px ${tokens.neutro.surface}`,
          },
          // Quien pidió no ver animaciones igual ve la foto grande, sin el viaje.
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {nombre.charAt(0)}
      </Avatar>
    </Box>
  );
}
